import React from 'react';
import { useOutletContext } from 'react-router-dom';
import { HolisticStudent } from '../../../types';

const TeacherTab: React.FC = () => {
  const { student } = useOutletContext<{ student: HolisticStudent }>();
  const obs = student.teacherObservation;

  const strengthTags = [
    ...obs.lp_strengths_t1.split(',').map(s => s.trim()),
    ...obs.lp_strengths_t2.split(',').map(s => s.trim()),
  ].filter(Boolean);

  const challengeTags = [
    ...obs.lp_challenges_t1.split(',').map(s => s.trim()),
    ...obs.lp_challenges_t2.split(',').map(s => s.trim()),
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      {/* Tags Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Strengths */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h4 className="text-sm font-semibold text-emerald-700 mb-3">Strength Tags</h4>
          <div className="flex flex-wrap gap-2">
            {strengthTags.map((tag, i) => (
              <span key={i} className="inline-block px-3 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Challenges */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h4 className="text-sm font-semibold text-amber-700 mb-3">Challenge Tags</h4>
          <div className="flex flex-wrap gap-2">
            {challengeTags.map((tag, i) => (
              <span key={i} className="inline-block px-3 py-1 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Observations */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h4 className="text-sm font-semibold text-gray-700 mb-4">Teacher Observations</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ObservationBlock label="Observations" t1={obs.lp_observations_t1} t2={obs.lp_observations_t2} />
          <ObservationBlock label="Suggestions" t1={obs.lp_suggestions_t1} t2={obs.lp_suggestions_t2} />
        </div>
      </div>

      {/* AI Summary */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl border border-indigo-100 p-6">
        <h4 className="text-sm font-semibold text-indigo-700 mb-3">AI Summarized Insights</h4>
        <div className="space-y-2 text-sm text-gray-700">
          <p>
            <span className="font-medium text-indigo-600">Strengths:</span>{' '}
            The student shows strong abilities in {strengthTags.slice(0, 3).join(', ').toLowerCase()}.
          </p>
          <p>
            <span className="font-medium text-amber-600">Areas for Growth:</span>{' '}
            Focus areas include {challengeTags.slice(0, 2).join(' and ').toLowerCase()}.
          </p>
          <p>
            <span className="font-medium text-blue-600">Recommendation:</span>{' '}
            {obs.lp_suggestions_t2}
          </p>
        </div>
      </div>
    </div>
  );
};

const ObservationBlock: React.FC<{ label: string; t1: string; t2: string }> = ({ label, t1, t2 }) => (
  <div>
    <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2">{label}</h5>
    <div className="space-y-2">
      <div>
        <span className="inline-block text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded mb-1">Term 1</span>
        <p className="text-sm text-gray-600">{t1}</p>
      </div>
      <div>
        <span className="inline-block text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded mb-1">Term 2</span>
        <p className="text-sm text-gray-600">{t2}</p>
      </div>
    </div>
  </div>
);

export default TeacherTab;
